import { Component, OnInit, AfterViewInit, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { CountUpDirective } from '../../directives/count-up.directive';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-statistics',
  standalone: true,
  imports: [CommonModule, MatIconModule, CountUpDirective, TranslateModule],
  template: `
    <section class="section statistics-section">
      <div class="container">
        <div class="stats-grid">
          <div class="stat-card" *ngFor="let stat of statistics" scrollAnimation>
            <mat-icon class="stat-icon">{{ stat.icon }}</mat-icon>
            <div class="stat-wrapper">
              <!-- Animated counter element -->
              <div class="stat-value" #statValue [attr.data-target]="stat.value" [attr.data-decimals]="stat.decimals || 0">0</div>
              <div class="stat-suffix" *ngIf="stat.suffix">{{ stat.suffix }}</div>
            </div>
            <!-- Apply the translate pipe to the label -->
            <div class="stat-label">{{ stat.label | translate }}</div>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .statistics-section {
      background: #6a1b9a;
      color: white;
      position: relative;
      overflow: hidden;
      padding: 0 0 4rem 0;
    }
    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 2rem;
      text-align: center;
      position: relative;
      z-index: 1;
    }
    .stat-card {
      padding: 2rem;
      background: rgba(255, 255, 255, 0.1);
      border-radius: var(--border-radius);
      backdrop-filter: blur(10px);
      transition: transform 0.3s ease;
    }
    .stat-card:hover {
      transform: translateY(-5px);
    }
    .stat-icon {
      font-size: 2.5rem;
      width: 2.5rem;
      height: 2.5rem;
      margin-bottom: 1rem;
      color: white;
    }
    .stat-wrapper {
      display: flex;
      align-items: baseline;
      justify-content: center;
      gap: 0.25rem;
    }
    .stat-value {
      font-size: 2.5rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
      display: inline-block;
    }
    .stat-label {
      font-size: 1.1rem;
      opacity: 0.9;
    }
    .stat-suffix {
      font-size: 1.1rem;
      opacity: 0.9;
      color: white;
    }
    @media (max-width: 768px) {
      .stats-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    @media (max-width: 480px) {
      .stats-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class StatisticsComponent implements OnInit, AfterViewInit {
  // Updated statistics array with translation keys for labels
  statistics = [
    { icon: 'public', value: 3, label: 'STATISTICS.COUNTRIES', suffix: '', decimals: 0 },
    { icon: 'groups', value: 10, label: 'STATISTICS.CUSTOMERS', suffix: '+', decimals: 0 },
    { icon: 'store', value: 700, label: 'STATISTICS.BRANCHES', suffix: '+', decimals: 0 },
    { icon: 'people', value: 24000, label: 'STATISTICS.EMPLOYEES', suffix: '+', decimals: 0 },
    { icon: 'attach_money', value: 150, label: 'STATISTICS.TOTAL_TRANSACTIONS', suffix: 'million - USD', decimals: 0 }
  ];

  // Use Angular's ViewChildren to get references to all stat-value elements
  @ViewChildren('statValue') statValues!: QueryList<ElementRef>;

  private observer: IntersectionObserver;

  constructor(private _translate: TranslateService) {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const statEl = entry.target as HTMLElement;
            const targetValue = parseFloat(statEl.getAttribute('data-target') || '0');
            const decimals = parseInt(statEl.getAttribute('data-decimals') || '0', 10);
            this.animateValue(statEl, 0, targetValue, decimals);
            this.observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px'
      }
    );
  }

  ngOnInit() {}

  ngAfterViewInit(): void {
    // Use ViewChildren to observe each stat-value element reliably
    setTimeout(() => {
      this.statValues.forEach((stat: ElementRef) => {
        this.observer.observe(stat.nativeElement);
      });
    }, 100);
  }

  private animateValue(element: HTMLElement, start: number, end: number, decimals: number) {
    let startTimestamp: number | null = null;
    const duration = 2000;

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = start + (end - start) * easeOutQuart;

      // For decimals=0, use floor; otherwise use toFixed
      element.textContent = decimals === 0 ? Math.floor(currentValue).toFixed(decimals) : currentValue.toFixed(decimals);

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        element.textContent = end.toFixed(decimals);
      }
    };

    window.requestAnimationFrame(step);
  }
}
